import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../config/api";
import toast from "react-hot-toast";
import {
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaMotorcycle,
  FaStar,
  FaRoute,
  FaCreditCard,
  FaMoneyBillWave,
} from "react-icons/fa";
import MapContainer from "../components/MapContainer";
import { cleanAddress } from "../utils/addressUtils";

const TripDetails = () => {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        const response = await api.get(`/api/trips/${id}`);
        setTrip(response.data.trip);
        // Reset selected seats when trip data changes
        if (response.data.trip) {
          const availableSeats = response.data.trip.availableSeats - response.data.trip.bookedSeats;
          setSelectedSeats(prev => Math.min(prev, Math.max(1, availableSeats)));
        }
      } catch (error) {
        console.error("Fetch trip error:", error);
        toast.error("Failed to load trip details");
      } finally {
        setLoading(false);
      }
    };

    fetchTrip();
  }, [id]);

  const handleBookTrip = async () => {
    if (!user) {
      toast.error("Please log in to book a trip");
      navigate("/login");
      return;
    }

    if (user.role !== "passenger") {
      toast.error("Only passengers can book trips");
      return;
    }

    if (!trip) {
      toast.error("Trip data not loaded. Please try again.");
      return;
    }

    // Accept both array [lng,lat] and object {lat,lng}
    const originCoords = trip.origin?.coordinates;
    const destCoords = trip.destination?.coordinates;

    const toLatLng = (c) => {
      if (!c) return null;
      if (Array.isArray(c) && c.length >= 2) {
        return { lat: parseFloat(c[1]), lng: parseFloat(c[0]) };
      }
      if (typeof c === 'object' && c.lat != null && c.lng != null) {
        return { lat: parseFloat(c.lat), lng: parseFloat(c.lng) };
      }
      return null;
    };

    const originLL = toLatLng(originCoords);
    const destLL = toLatLng(destCoords);

    if (!originLL || !destLL || isNaN(originLL.lat) || isNaN(originLL.lng) || isNaN(destLL.lat) || isNaN(destLL.lng)) {
      toast.error("Trip location data is invalid.");
      return;
    }

    // Validate selected seats
    const availableSeats = trip.availableSeats - trip.bookedSeats;
    if (selectedSeats > availableSeats) {
      toast.error(`Only ${availableSeats} seat${availableSeats !== 1 ? 's' : ''} available`);
      return;
    }

    if (selectedSeats < 1) {
      toast.error('Please select at least 1 seat');
      return;
    }

    setBookingLoading(true);
    try {
      const bookingData = {
        tripId: trip._id,
        seats: selectedSeats,
        pickupLocation: {
          address: trip.origin.address,
          coordinates: originLL,
        },
        dropoffLocation: {
          address: trip.destination.address,
          coordinates: destLL,
        },
      };

      const response = await api.post("/api/bookings", bookingData);
      
      // Check if booking was actually created (even if response has error status)
      if (response.data && response.data.booking) {
        toast.success("Booking request sent successfully!");
        navigate("/my-bookings");
      } else {
        toast.success("Booking request sent successfully!");
        navigate("/my-bookings");
      }
    } catch (error) {
      console.error("Booking error:", error.response?.data || error.message);
      
      // Check if it's a duplicate booking error
      const errorMessage = error.response?.data?.message || '';
      if (errorMessage.includes('already have a booking')) {
        toast.error("You already have a booking for this trip");
        // Refresh trip data to show updated status
        const response = await api.get(`/api/trips/${id}`);
        setTrip(response.data.trip);
      } else if (error.response?.status === 500) {
        // Server error - check if booking might have been created anyway
        // Try to fetch user's bookings to see if booking exists
        try {
          const bookingsResponse = await api.get('/api/bookings/my-bookings');
          const existingBooking = bookingsResponse.data.bookings?.find(
            b => b.trip?._id === trip._id && 
            (b.status === 'pending' || b.status === 'accepted')
          );
          
          if (existingBooking) {
            toast.success("Booking request was sent successfully!");
            navigate("/my-bookings");
          } else {
            toast.error("Failed to book trip. Please try again.");
          }
        } catch (checkError) {
          toast.error("Failed to book trip. Please try again.");
        }
      } else {
        const message =
          error.response?.data?.message ||
          error.response?.data?.errors?.[0]?.msg ||
          "Failed to book trip. It may no longer be available.";
        toast.error(message);
      }
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-gray-600">Trip not found</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← Back to search
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Trip Details */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-6">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Trip Details
                  </h1>
                  <button
                    onClick={() => setShowMap(!showMap)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                  >
                    <FaRoute className="mr-2" />
                    {showMap ? "Hide Route" : "Show Route"}
                  </button>
                </div>

                {/* Map */}
                {showMap && (
                  <div className="mb-6">
                    <MapContainer
                      origin={trip.origin.coordinates}
                      destination={trip.destination.coordinates}
                      waypoints={trip.waypoints || []}
                      height="300px"
                      showRoute={true}
                    />
                  </div>
                )}

                {/* Route */}
                <div className="mb-6">
                  <div className="flex items-center mb-2">
                    <FaMapMarkerAlt className="text-green-500 mr-2" />
                    <span className="font-medium">{cleanAddress(trip.origin.address)}</span>
                  </div>
                  {trip.waypoints && trip.waypoints.length > 0 && (
                    <div className="ml-6 mb-2 space-y-1">
                      {trip.waypoints
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((wp, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-600">
                            <FaMapMarkerAlt className="text-yellow-500 mr-2" />
                            <span>Stop {idx + 1}: {cleanAddress(wp.address)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="text-red-500 mr-2" />
                    <span className="font-medium">
                      {cleanAddress(trip.destination.address)}
                    </span>
                  </div>
                </div>

                {/* Departure Time */}
                <div className="mb-6">
                  <div className="flex items-center text-gray-600">
                    <FaClock className="mr-2" />
                    <span>
                      Departure: {new Date(trip.departureTime).toLocaleString()}
                    </span>
                  </div>
                  {trip.route && (
                    <div className="text-sm text-gray-500 mt-1">
                      Distance: {trip.route.distance?.toFixed(1)} km • Duration:{" "}
                      {trip.route.duration} minutes
                    </div>
                  )}
                </div>

                {/* Description */}
                {trip.description && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-800 mb-2">
                      Description
                    </h3>
                    <p className="text-gray-600">{trip.description}</p>
                  </div>
                )}

                {/* Preferences */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-2">
                    Preferences
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        trip.preferences.smoking
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {trip.preferences.smoking
                        ? "Smoking Allowed"
                        : "No Smoking"}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        trip.preferences.music
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {trip.preferences.music ? "Music On" : "Music Off"}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        trip.preferences.chat
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {trip.preferences.chat ? "Chat Friendly" : "Quiet Ride"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Rider Info & Booking */}
            <div className="lg:col-span-1">
              {/* Rider Card */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="font-semibold text-gray-800 mb-4">
                  Rider Information
                </h3>

                <div className="flex items-center mb-4">
                  <img
                    src={trip.rider.photo || "/default-avatar.png"}
                    alt={trip.rider.name}
                    className="w-16 h-16 rounded-full mr-4 object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {trip.rider.name}
                    </h4>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaStar className="text-yellow-400 mr-1" />
                      <span>{trip.rider.rating?.toFixed(1) || "N/A"}</span>
                      <span className="mx-2">•</span>
                      <span>{trip.rider.totalRides || 0} rides</span>
                    </div>
                    {user?.role === 'passenger' && (
                      <div className="mt-2">
                        <a
                          href={`tel:${trip.rider.phone || ''}`}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Contact Rider
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vehicle Info */}
                {trip.rider.vehicleInfo && (
                  <div className="mb-4">
                    <h5 className="font-medium text-gray-700 mb-2">
                      Vehicle Details
                    </h5>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center">
                        {['car', 'sedan', 'hatchback', 'suv'].includes(trip.rider.vehicleInfo.type) ? (
                          <span className="mr-2">🚗</span>
                        ) : (
                          <FaMotorcycle className="mr-2 text-blue-600" />
                        )}
                        <span className="capitalize">
                          {trip.rider.vehicleInfo.type} - {trip.rider.vehicleInfo.model}
                        </span>
                      </div>
                      <div>Year: {trip.rider.vehicleInfo.year}</div>
                      <div>Color: {trip.rider.vehicleInfo.color}</div>
                      <div>Plate: {trip.rider.vehicleInfo.plateNumber}</div>
                      {trip.rider.vehicleInfo.seats && (
                        <div>Seats: {trip.rider.vehicleInfo.seats} passenger seats</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Verification Status */}
                <div className="mb-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      trip.rider.verificationStatus === "verified"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {trip.rider.verificationStatus === "verified"
                      ? "Verified Rider"
                      : "Pending Verification"}
                  </span>
                </div>
              </div>

              {/* Booking Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center mb-4">
                  {user?.role === 'passenger' ? (
                    <>
                      <div className="text-3xl font-bold text-blue-600">
                        ₹{((trip.price + Math.round(trip.price * 0.1 * 100) / 100) * selectedSeats).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {selectedSeats > 1 ? `for ${selectedSeats} seats` : 'per seat'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        ₹{(trip.price + Math.round(trip.price * 0.1 * 100) / 100).toFixed(2)} per seat
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-bold text-blue-600">
                        ₹{trip.price}
                      </div>
                      <div className="text-sm text-gray-600">per seat</div>
                    </>
                  )}
                </div>

                {/* Seat Selection - Only for passengers */}
                {user?.role === 'passenger' && trip.status === "open" && trip.availableSeats > trip.bookedSeats && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Seats
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedSeats(Math.max(1, selectedSeats - 1))}
                        disabled={selectedSeats <= 1}
                        className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        max={trip.availableSeats - trip.bookedSeats}
                        value={selectedSeats}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          const maxSeats = trip.availableSeats - trip.bookedSeats;
                          setSelectedSeats(Math.min(Math.max(1, value), maxSeats));
                        }}
                        className="w-20 text-center text-lg font-semibold border-2 border-gray-300 rounded-md py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setSelectedSeats(Math.min(trip.availableSeats - trip.bookedSeats, selectedSeats + 1))}
                        disabled={selectedSeats >= trip.availableSeats - trip.bookedSeats}
                        className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        +
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {trip.availableSeats - trip.bookedSeats} seat{trip.availableSeats - trip.bookedSeats !== 1 ? 's' : ''} available
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Available Seats</span>
                    <span>{trip.availableSeats - trip.bookedSeats}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${
                          trip.availableSeats > 0
                            ? ((trip.availableSeats - trip.bookedSeats) /
                                trip.availableSeats) *
                              100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>

                {trip.status === "open" &&
                trip.availableSeats > trip.bookedSeats ? (
                  <button
                    onClick={handleBookTrip}
                    disabled={bookingLoading || user?.role !== "passenger"}
                    className={`w-full py-3 px-4 rounded-md text-white font-medium ${
                      bookingLoading || user?.role !== "passenger"
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {bookingLoading ? "Booking..." : "Book This Ride"}
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full py-3 px-4 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed"
                  >
                    {trip.status !== "open"
                      ? "Trip Not Available"
                      : "Fully Booked"}
                  </button>
                )}

                {user?.role === "rider" && (
                  <p className="text-sm text-gray-500 text-center mt-2">
                    You can only book rides as a passenger
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripDetails;